import 'quill/dist/quill.snow.css';
import './rich-text-editor.css';

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { next } from '@ember/runloop';

import Quill from 'quill/core';
import Toolbar from 'quill/modules/toolbar';
import History from 'quill/modules/history';
import Snow from 'quill/themes/snow';
import Bold from 'quill/formats/bold';
import Italic from 'quill/formats/italic';
import Underline from 'quill/formats/underline';

import type LocaleModel from 'admin-panel/models/locale';

interface UiInputGroupRichTextEditorSignature {
  Element: HTMLDivElement;
  Args: {
    Named: {
      id: string;
      hasError?: boolean;
      ariaDescribedby?: string;
      iconRight?: string;
      defaultValue: string;
      selectedLocale?: LocaleModel;
      onTextChange?: (value: string) => void;
    };
  };
  Blocks: {
    default: [];
  };
}

export default class UiInputGroupRichTextEditorComponent extends Component<UiInputGroupRichTextEditorSignature> {
  @tracked
  quill: Quill | undefined;

  @tracked
  hasFocus = false;

  @tracked
  hasNoUndos = true;

  @tracked
  hasNoRedos = true;

  willDestroy() {
    super.willDestroy();

    this.quill?.off('text-change', this.handleTextChange);
    this.quill?.off('editor-change', this.handleEditorChange);
  }

  @action
  initializeQuill(element: Element) {
    Quill.register({
      'modules/toolbar': Toolbar,
      'modules/history': History,
      'themes/snow': Snow,
      'formats/bold': Bold,
      'formats/italic': Italic,
      'formats/underline': Underline,
    });

    this.quill = new Quill(element as HTMLElement, {
      theme: 'snow',
      modules: {
        toolbar: '#toolbar-container',
        history: {
          userOnly: true,
        },
      },
    });

    this.quill.root.setAttribute(
      'data-test-ui-input-group-rich-text-editor-textfield',
      ''
    );

    this.insertTextValue();

    this.quill.on('text-change', this.handleTextChange);
    this.quill.on('editor-change', this.handleEditorChange);
  }

  @action
  handleTextChange() {
    next(() => {
      this.args.onTextChange?.((this.quill as Quill).root.innerHTML);
    });

    this.hasNoUndos = !this.quill?.history.stack.undo.length;
    this.hasNoRedos = !this.quill?.history.stack.redo.length;
  }

  @action
  handleEditorChange() {
    this.hasFocus = !!this.quill?.hasFocus();
  }

  @action
  insertTextValue() {
    this.quill?.clipboard.dangerouslyPasteHTML(this.args.defaultValue);
    this.quill?.blur();
  }

  @action
  onLocaleChange() {
    this.clearHistory();

    this.insertTextValue();
  }

  @action
  clearHistory() {
    this.quill?.history.clear();
  }

  @action
  undo() {
    this.quill?.history.undo();
  }

  @action
  redo() {
    this.quill?.history.redo();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::InputGroup::RichTextEditor': typeof UiInputGroupRichTextEditorComponent;
    'ui/input-group/rich-text-editor': typeof UiInputGroupRichTextEditorComponent;
  }
}
